import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Pause, Trash2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { H1, Sub, IconBtn } from "./_shared";

const PATH = { match: "matches", tournament: "tournaments", league: "leagues", lesson: "lessons" };
const TITLE = { match: "Matches", tournament: "Tournaments", league: "Leagues", lesson: "Lessons" };

export default function EventsSection({ kind, verifiable }) {
  const [items, setItems] = useState([]);
  const path = PATH[kind];
  const load = useCallback(async () => {
    const { data } = await api.get(`/${path}`);
    setItems(data);
  }, [path]);
  useEffect(() => { load(); }, [load]);
  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This will also delete registrations.`)) return;
    try { await api.delete(`/admin/${path}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const cancel = async (id) => {
    try { await api.post(`/admin/${kind}s/${id}/cancel`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const verify = async (id) => {
    try { await api.post(`/admin/${kind}s/${id}/verify`); toast.success("Verified"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  return (
    <div data-testid={`cp-events-${kind}`}>
      <H1>{TITLE[kind]}</H1>
      <Sub>Cancel, verify or hard-delete any {kind}.</Sub>
      <div className="mt-6 pp-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-white/5">
            <tr>
              <th className="text-start px-4 py-3">Title</th>
              <th className="text-start px-4 py-3">Sport</th>
              <th className="text-start px-4 py-3">Date</th>
              <th className="text-start px-4 py-3">Status</th>
              <th className="text-end px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-white/5" data-testid={`cp-${kind}-${it.id}`}>
                <td className="px-4 py-2"><div className="font-medium">{it.title}</div><div className="text-xs text-zinc-500">{it.id.slice(0, 8)}</div></td>
                <td className="px-4 py-2 text-zinc-400">{it.sport?.name || "—"}</td>
                <td className="px-4 py-2 text-zinc-400">{new Date(it.start_time || it.start_date).toLocaleDateString()}</td>
                <td className="px-4 py-2">
                  <span className="pp-chip">{it.status || "open"}</span>
                  {it.is_verified ? <ShieldCheck size={12} className="text-[#00E676] inline ms-2" /> : null}
                </td>
                <td className="px-4 py-2 text-end">
                  <div className="inline-flex gap-1">
                    {verifiable && !it.is_verified && <button onClick={() => verify(it.id)} data-testid={`cp-${kind}-verify-${it.id}`} className="pp-btn-secondary px-3 py-1 rounded-lg text-xs">Verify</button>}
                    {it.status !== "cancelled" && <IconBtn onClick={() => cancel(it.id)} title="Cancel" data-testid={`cp-${kind}-cancel-${it.id}`}><Pause size={14} /></IconBtn>}
                    <IconBtn onClick={() => del(it.id, it.title)} danger title="Delete" data-testid={`cp-${kind}-delete-${it.id}`}><Trash2 size={14} /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="text-center text-zinc-500 py-10">Nothing here.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
